<template>
  <div>
    <p><small>Rules automatically assign users to this subgroup based on the users attributes.</small></p>

    <div v-if="loading" class="text-center">
      <i class="fa fa-spinner fa-spin fa-2x"></i>
    </div>

    <div class="alert alert-warning" v-if="errorMessage">
      {{ errorMessage }}
    </div>

    <div v-if="!loading">
      <div v-if="rules.length === 0" class="alert alert-info">
        No rules have been created yet. Click "Add Rule" to create one.
      </div>

      <div v-if="rules.length > 0">
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Rule Type</th>
              <th>Schools</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rule in rules" :key="rule.uuid">
              <td>{{ rule.rule_type }}</td>
              <td>
                <span v-if="rule.rule_config && rule.rule_config.school_uuids && rule.rule_config.school_uuids.length > 0">
                  {{ rule.rule_config.school_uuids.length }} school(s)
                </span>
                <span v-else class="text-muted">No schools selected</span>
              </td>
              <td>
                <span :class="rule.enabled ? 'label label-success' : 'label label-default'">
                  {{ rule.enabled ? 'Enabled' : 'Disabled' }}
                </span>
              </td>
              <td>
                <button class="btn btn-xs btn-default" @click="editRule(rule)" :disabled="!canManageSubgroups">
                  Edit
                </button>
                <button 
                  class="btn btn-xs" 
                  :class="rule.enabled ? 'btn-warning' : 'btn-success'"
                  @click="toggleRuleStatus(rule)" 
                  :disabled="!canManageSubgroups"
                >
                  {{ rule.enabled ? 'Disable' : 'Enable' }}
                </button>
                <button class="btn btn-xs btn-danger" @click="deleteRule(rule)" :disabled="!canManageSubgroups">
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="form-group">
        <button class="btn btn-primary" @click="showAddRuleForm" :disabled="!canManageSubgroups">
          Add Rule
        </button>
        <button class="btn btn-default" @click="$emit('close')">
          Close
        </button>
      </div>

      <!-- Add/Edit Rule Form -->
      <div v-if="showRuleForm" class="panel panel-default">
        <div class="panel-heading">
          <h4>{{ editingRule ? 'Edit Rule' : 'Add Rule' }}</h4>
        </div>
        <div class="panel-body">
          <form @submit.prevent="saveRule">
            <div class="form-group">
              <div class="checkbox">
                <label>
                  <input type="checkbox" v-model="ruleForm.enabled">
                  Enabled
                </label>
              </div>
              <p class="help-block">When enabled, this rule will automatically assign users to this subgroup.</p>
            </div>


            <div class="form-group">
              <label for="rule_type">Rule Type</label>
              <select id="rule_type" class="form-control" v-model="ruleForm.rule_type" required disabled>
                <option value="school">School-based</option>
              </select>
              <p class="help-block">Currently only school-based rules are supported.</p>
            </div>

            <div class="form-group schools">
              <legend>Schools</legend>
              <div v-if="loadingSchools" class="text-muted">
                <i class="fa fa-spinner fa-spin"></i> Loading schools...
              </div>
              <div v-else>
                <div v-if="schools.length === 0" class="alert alert-warning">
                  No schools found in this domain.
                </div>
                <div v-else>
                  <div class="schools-filter-controls">
                    <div class="form-group">
                      <input 
                        type="text" 
                        class="form-control" 
                        v-model="schoolSearchTerm" 
                        placeholder="Search schools..."
                        aria-label="Filter schools"
                      >
                    </div>
                    <div class="filter-options">
                      <div class="checkbox">
                        <label>
                          <input type="checkbox" v-model="showSelectedOnly">
                          Show selected only
                        </label>
                      </div>
                      <div class="selected-count">
                        <strong>{{ ruleForm.school_uuids.length }}</strong> of <strong>{{ schools.length }}</strong> schools selected
                      </div>
                    </div>
                  </div>
                  <div v-if="filteredSchools.length === 0" class="text-muted">
                    No schools match your search.
                  </div>
                  <div v-else class="schools-list">
                    <div class="checkbox" v-for="school in filteredSchools" :key="school.uuid">
                      <label>
                        <input 
                          type="checkbox" 
                          :value="school.uuid" 
                          :aria-label="'select school: ' + school.name"
                          v-model="ruleForm.school_uuids"
                        >
                        {{ school.name }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <button type="submit" class="btn btn-primary" :disabled="saving">
                <span v-if="saving"><i class="fa fa-spinner fa-spin"></i> Saving...</span>
                <span v-else>Save</span>
              </button>
              <button type="button" class="btn btn-default" @click="cancelRuleForm" :disabled="saving">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Permissions } from '@/client_auth/permissions';

export default {
  name: 'SubgroupRules',
  props: {
    subgroup: {
      type: Object,
      required: true,
    },
    domainUuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadingSchools: false,
      saving: false,
      rules: [],
      schools: [],
      errorMessage: '',
      showRuleForm: false,
      editingRule: null,
      ruleForm: {
        rule_type: 'school',
        school_uuids: [],
        enabled: true,
      },
      canManageSubgroups: false,
      schoolSearchTerm: '',
      showSelectedOnly: false,
    };
  },
  computed: {
    filteredSchools() {
      let filtered = this.schools;
      
      // Filter by selected only
      if (this.showSelectedOnly) {
        filtered = filtered.filter(school => 
          this.ruleForm.school_uuids.includes(school.uuid)
        );
      }
      
      // Filter by search term
      if (this.schoolSearchTerm) {
        const searchLower = this.schoolSearchTerm.toLowerCase();
        filtered = filtered.filter(school => 
          school.name.toLowerCase().includes(searchLower)
        );
      }
      
      return filtered;
    },
  },
  methods: {
    async fetchRules() {
      this.errorMessage = '';
      this.loading = true;
      try {
        const { data } = await this.$angularWorld.adminEntitiesApi.listSubgroupRules({
          domain_uuid: this.domainUuid,
          rule_type: 'school',
        });
        this.rules = data.filter(rule => rule.subgroup_uuid === this.subgroup.uuid);
      } catch (error) {
        console.error('Failed to fetch subgroup rules', error);
        this.errorMessage = 'Failed to fetch subgroup rules';
      } finally {
        this.loading = false;
      }
    },

    async fetchSchools() {
      this.loadingSchools = true;
      try {
        const response = await this.$angularWorld.graph.getDomainSchools(this.domainUuid);
        if (Array.isArray(response)) {
          this.schools = response;
        } else {
          this.schools = [];
          this.errorMessage = 'Failed to fetch schools';
        }
      } catch (error) {
        console.error('Failed to fetch schools', error);
        this.errorMessage = 'Failed to fetch schools';
      } finally {
        this.loadingSchools = false;
      }
    },

    showAddRuleForm() {
      this.editingRule = null;
      this.ruleForm = {
        rule_type: 'school',
        school_uuids: [],
        enabled: true,
      };
      this.schoolSearchTerm = '';
      this.showSelectedOnly = false;
      this.showRuleForm = true;
      if (this.schools.length === 0) {
        this.fetchSchools();
      }
    },

    editRule(rule) {
      this.editingRule = rule;
      this.ruleForm = {
        rule_type: rule.rule_type,
        school_uuids: rule.rule_config && rule.rule_config.school_uuids ? [...rule.rule_config.school_uuids] : [],
        enabled: rule.enabled,
      };
      this.schoolSearchTerm = '';
      this.showSelectedOnly = false;
      this.showRuleForm = true;
      if (this.schools.length === 0) {
        this.fetchSchools();
      }
    },

    async saveRule() {
      this.errorMessage = '';
      this.saving = true;
      try {
        const ruleData = {
          subgroup_uuid: this.subgroup.uuid,
          domain_uuid: this.domainUuid,
          rule_type: this.ruleForm.rule_type,
          rule_config: {
            school_uuids: this.ruleForm.school_uuids,
          },
          enabled: this.ruleForm.enabled,
        };

        if (this.editingRule) {
          ruleData.uuid = this.editingRule.uuid;
          await this.$angularWorld.adminEntitiesApi.updateSubgroupRule(ruleData);
        } else {
          await this.$angularWorld.adminEntitiesApi.createSubgroupRule(ruleData);
        }

        this.showRuleForm = false;
        this.editingRule = null;
        await this.fetchRules();
      } catch (error) {
        console.error('Failed to save subgroup rule', error);
        this.errorMessage = 'Failed to save subgroup rule';
      } finally {
        this.saving = false;
      }
    },

    cancelRuleForm() {
      this.showRuleForm = false;
      this.editingRule = null;
    },

    async toggleRuleStatus(rule) {
      this.errorMessage = '';
      try {
        const updatedRule = {
          uuid: rule.uuid,
          enabled: !rule.enabled,
          subgroup_uuid: rule.subgroup_uuid,
          domain_uuid: rule.domain_uuid,
          rule_type: rule.rule_type,
          rule_config: rule.rule_config,
        };
        await this.$angularWorld.adminEntitiesApi.updateSubgroupRule(updatedRule);
        await this.fetchRules();
      } catch (error) {
        console.error('Failed to toggle rule status', error);
        this.errorMessage = 'Failed to toggle rule status';
      }
    },

    async deleteRule(rule) {
      this.errorMessage = '';
      // eslint-disable-next-line no-alert
      if (globalThis.confirm(`Are you sure you want to delete this rule?`)) {
        try {
          await this.$angularWorld.adminEntitiesApi.deleteSubgroupRule(rule.uuid);
          await this.fetchRules();
        } catch (error) {
          console.error('Failed to delete subgroup rule', error);
          this.errorMessage = 'Failed to delete subgroup rule';
        }
      }
    },

    async updatePermissions() {
      const hasPermission = await this.$angularWorld.auth.doesUserHavePermission([Permissions.UserOps]);
      this.canManageSubgroups = hasPermission;
    },
  },
  async mounted() {
    await this.fetchRules();
    await this.updatePermissions();
  },
  watch: {
    '$angularWorld.auth.currentUser'() {
      this.updatePermissions();
    },
  },
};
</script>
<style scoped>
legend {
  font-size: 1.4rem;
  font-weight: bold;
}

.schools-filter-controls {
  margin-bottom: 10px;
}

.filter-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.filter-options .checkbox {
  margin: 0;
}

.selected-count {
  color: #555;
  font-size: 0.95em;
}

.schools-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  padding: 10px;
  border-radius: 4px;
}
</style>